/**
 * MinorGates — UGMA / UTMA / plain Minor accounts.
 *
 * Ported from SPFx ProceduralGates:
 *   - _validateRequiredFields (Minor uses Individual fields)
 *   - _validateEntityRestrictions (MINOR_NO_ODP, UTMA_NO_ODP)
 *   - _validateRelationships (MINOR_GUARDIAN, UTMA_CUSTODIAN)
 */

import type { CartSnapshot, GateResult } from '../workflow-engine/types';
import {
  buildCipGate,
  buildFeatureRestrictionGates,
  buildOfacGate,
  buildRequiredFieldGates,
  getByRole,
  getPrimary,
  productHasOdp,
  type FieldSpec,
} from '../ProceduralGates';

const MINOR_FIELDS: ReadonlyArray<FieldSpec> = [
  { field: 'firstName', label: 'First name (minor)' },
  { field: 'lastName', label: 'Last name (minor)' },
  { field: 'taxId', label: 'SSN (minor)' },
  { field: 'dob', label: 'Date of birth (minor)' },
  { field: 'addr', label: 'Street address' },
  { field: 'city', label: 'City' },
  { field: 'state', label: 'State' },
  { field: 'zip', label: 'ZIP code' },
];

export function runMinorGates(cart: CartSnapshot): GateResult[] {
  const gates: GateResult[] = [];
  const primary = getPrimary(cart);
  if (!primary) return gates;

  gates.push(...buildRequiredFieldGates(cart, primary, MINOR_FIELDS));

  // Restrictions — no ODP
  gates.push(
    ...buildFeatureRestrictionGates(cart, [
      {
        gateCode: cart.entityType === 'UTMA' ? 'UTMA_NO_ODP' : 'MINOR_NO_ODP',
        label: cart.entityType === 'UTMA' ? 'UTMA cannot have ODP' : 'Minor cannot have ODP',
        predicate: productHasOdp,
        reason: `${cart.entityType ?? 'Minor'} accounts cannot have Overdraft Protection.`,
      },
    ]),
  );

  // Guardian / custodian linkage
  const guardians = getByRole(cart, 'custodian');
  const joints = getByRole(cart, 'joint');

  if (cart.entityType === 'UTMA' || cart.entityType === 'UGMA') {
    const hasCustodian = !!primary.custodianCifId || guardians.length > 0 || joints.length > 0;
    if (!hasCustodian) {
      gates.push({
        gateCode: 'UTMA_CUSTODIAN',
        entityType: cart.entityType ?? '',
        category: 'relationship',
        status: 'fail',
        label: 'Custodian required',
        reason: `${cart.entityType} accounts require a custodian linked to the account.`,
        remediationAction: { label: 'Add custodian', deepLinkStepId: 'people' },
      });
    }
  } else {
    // Plain Minor — guardian or joint owner required
    if (guardians.length === 0 && joints.length === 0) {
      gates.push({
        gateCode: 'MINOR_GUARDIAN',
        entityType: cart.entityType ?? '',
        category: 'relationship',
        status: 'fail',
        label: 'Guardian required',
        reason: 'Minor accounts require a guardian linked as joint owner.',
        remediationAction: { label: 'Add guardian', deepLinkStepId: 'people' },
      });
    }
  }

  gates.push(buildCipGate(cart));
  gates.push(buildOfacGate(cart));

  return gates;
}
